import { useCallback, useMemo } from 'react';

import { useServiceTypeTemplateCtx } from '@/app/(app)/settings/service_types/[id]/provider';
import { DayScheduleEditor, DaySlot } from '@/components/molecules/day-schedule-editor';
import { DayOfWeek, DAYS_OF_WEEK } from '@/consts/date-time';
import { groupBy } from '@/lib/group';
import { createUUID } from '@/lib/uuid';

export function ServiceTypeTemplateArrivalWindows() {
  const { serviceTypeTemplate, update } = useServiceTypeTemplateCtx();

  const grouped = useMemo(() => {
    const groupedByDay = groupBy(serviceTypeTemplate.arrival_windows || [], 'day_of_week');
    return DAYS_OF_WEEK.map((day) => ({
      day_of_week: day,
      slots: (groupedByDay.get(day) || [])
        .map((slot) => ({ ...slot, id: createUUID() }))
        .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    }));
  }, [serviceTypeTemplate.arrival_windows]);

  const updateDaySlots = useCallback(
    (day: DayOfWeek, slots: DaySlot[]) => {
      const newWindows = (serviceTypeTemplate.arrival_windows || []).filter((w) => w.day_of_week !== day);
      const newSlots = slots.map((slot) => ({
        day_of_week: day,
        start_time: slot.start_time,
        end_time: slot.end_time,
      }));
      update({ arrival_windows: [...newWindows, ...newSlots] });
    },
    [serviceTypeTemplate.arrival_windows, update]
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-heading-md'>Arrival Windows</h2>
        <p className='text-sm text-muted-foreground'>
          Define time windows when technicians can arrive for this service.
        </p>
      </div>
      <div className='flex flex-col'>
        {grouped.map((day) => (
          <DayScheduleEditor
            key={day.day_of_week}
            schedule={day}
            update={(slots) => updateDaySlots(day.day_of_week, slots)}
          />
        ))}
      </div>
    </div>
  );
}
